import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import DealLink from '../shared/DealLink.jsx'
import QueueHeader from '../shared/QueueHeader.jsx'

/**
 * To send back (spec 20261102): items the customer asked for back, not yet
 * posted. One card per quote with who and where, then each item with
 * "Posted" (the item is on its way, so it is done) and "Undo" (the customer
 * changed their mind, so it returns to Issue Trades). No tracking number is
 * taken. Staff add it as a quote note.
 */
function ToSendBack() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyItemId, setBusyItemId] = useState(null)

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/warehouse/returns/to-send-back')
      setDeals(response.deals || [])
    } catch (err) {
      setError(err.message || 'Failed to load items to send back')
    } finally {
      setLoading(false)
    }
  }

  // The item has moved when this resolves. A failed quote note is a warning, not an error.
  async function moveItem(deal, item, action, confirmText) {
    if (!confirm(confirmText)) return
    setBusyItemId(item.quoteItemId)
    setError(null)
    try {
      const response = await api.post(`/api/warehouse/returns/${action}`, {
        dealId: deal.quoteId,
        quoteItemId: item.quoteItemId
      })
      if (response?.warnings?.length) alert(response.warnings.join('\n'))
      await loadDeals()
    } catch (err) {
      setError(err.message || 'Could not update the item')
    } finally {
      setBusyItemId(null)
    }
  }

  const markPosted = (deal, item) =>
    moveItem(deal, item, 'posted', `Mark "${item.name}" as posted?\n\nThis cannot be undone here.`)
  const undoReturn = (deal, item) =>
    moveItem(deal, item, 'undo', `Cancel the return of "${item.name}"?\n\nIt goes back to Issue Trades.`)

  const header = (
    <QueueHeader
      title="To Send Back"
      description="Items the customer wants back. The customer books the return. Press Posted when it has gone."
    >
      <button className="btn btn-sm btn-secondary" onClick={loadDeals}>↻ Refresh</button>
    </QueueHeader>
  )

  if (loading) {
    return (
      <div>
        {header}
        <div className="card" style={{ padding: 'var(--space-4)' }}>Loading...</div>
      </div>
    )
  }

  return (
    <div>
      {header}
      {error && (
        <div role="alert" style={{ color: '#E5625E', marginBottom: 'var(--space-3)' }}>{error}</div>
      )}

      {deals.length === 0 ? (
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--muted)' }}>
          Nothing to send back.
        </div>
      ) : (
        deals.map(deal => (
          <div className="card" key={deal.quoteId} style={{ marginBottom: 'var(--space-4)' }}>
            <div className="card-header">
              <h3 className="card-title">{deal.customerName || deal.customerEmail || 'Customer'}</h3>
              <DealLink dealId={deal.quoteId} reference={deal.reference} newTab />
            </div>
            <div style={{ padding: 'var(--space-4)' }}>
              <div style={{ marginBottom: 'var(--space-3)' }}>
                {deal.address || <span style={{ color: 'var(--muted)' }}>No address on file</span>}
              </div>
              {deal.items.map(item => (
                <div
                  key={item.quoteItemId}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 12, flexWrap: 'wrap', padding: '8px 0',
                    borderTop: '1px solid var(--border, #E7E5E4)'
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {item.name}{item.quantity > 1 ? ` x ${item.quantity}` : ''}
                  </span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-sm btn-success"
                      disabled={busyItemId === item.quoteItemId}
                      onClick={() => markPosted(deal, item)}
                    >
                      Posted
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      disabled={busyItemId === item.quoteItemId}
                      onClick={() => undoReturn(deal, item)}
                    >
                      Undo
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default ToSendBack
